import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../ui';
import type { CompanionEntry } from './companions';
import { COMPANION_GLYPHS } from './glyphs';

export interface BhajanCompanionsProps {
  /** From bhajanCompanions(). Rendered in the order given; never re-sorted. */
  entries: CompanionEntry[];
  /** Lead-in text, and the nav landmark's accessible name. */
  label?: string;
  /**
   * Layout only. Callers pass 'mb-10 print-hide'.
   *
   * MUST NOT contain `grid`. The print stylesheet's
   * `.bhajan-print-root > div.grid` rule has higher specificity than
   * `.print-hide` and both are `!important`, so a top-level grid inside the
   * bhajan article prints as a stray gap even while marked hidden. A test
   * asserts the rendered class list has no `grid` token.
   */
  className?: string;
}

/**
 * Every place this bhajan can send you, in one row.
 *
 * Chips, not cards. The per-item cost is one pill, so the row reads as a row at
 * one entry and at six; the banner card it replaces cost 112px each and was
 * only ever tolerable because just four bhajans in the catalog had a score.
 * Deliberately no subtitle on a chip.
 *
 * No `loading` prop and no skeleton chip. Distraction-free needs no data, so the
 * row exists at first paint and the edge-gated chips append when the bhajan's
 * neighbourhood resolves. A placeholder pill of an unknowable count would trade
 * a small settle for a wrong-sized one.
 */
export const BhajanCompanions: React.FC<BhajanCompanionsProps> = ({
  entries,
  label = 'More for this bhajan',
  className,
}) => {
  const labelId = useId();
  if (entries.length === 0) return null;

  return (
    <nav
      aria-labelledby={labelId}
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}
    >
      <span id={labelId} className="text-small text-text-tertiary select-none">
        {label}
      </span>
      {/* role="list" explicitly: Tailwind's preflight sets list-style:none, which
          drops list semantics in Safari/VoiceOver — and the item count is the
          most useful thing this row can announce. */}
      <ul role="list" className="flex flex-wrap items-center gap-2">
        {entries.map((e) => (
          <li key={e.kind}>
            {/*
              bg-bg-2 + hover:bg-bg-3, NOT hover:bg-bg-3/60 — Tailwind opacity
              modifiers on this project's CSS-variable colour tokens emit no CSS
              at all, which is why the card being replaced has silently had no
              hover state.

              No overflow-hidden anywhere in nav/ul/li/a: the global focus ring
              draws 2px OUTSIDE the element and would be clipped. And no
              outline-offset override — that fix is for an edge-to-edge control
              inside a clipping card, and here it would draw an inset ring inside
              a visible border and read as a doubled one.
            */}
            <Link
              to={e.to}
              aria-label={e.ariaLabel}
              className="group inline-flex h-10 items-center gap-2 rounded-full border border-light bg-bg-2 px-4 text-small font-medium text-text-primary no-underline transition-colors duration-150 hover:border-strong hover:bg-bg-3"
            >
              <span className="shrink-0 text-text-secondary transition-colors duration-150 group-hover:text-accent-gold">
                {COMPANION_GLYPHS[e.kind]}
              </span>
              <span className="whitespace-nowrap">{e.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default BhajanCompanions;
